import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, query, setDoc, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import { prepararCompania, ordenarCompanias } from "../services/companiasTenant";

function normalizarSlug(v) {
  return String(v || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function validarImagen(file) {
  if (!file) return true;
  if (!file.type?.startsWith("image/")) {
    alert("El archivo seleccionado debe ser una imagen.");
    return false;
  }
  if (file.size > 5 * 1024 * 1024) {
    alert("El logo no puede superar 5 MB.");
    return false;
  }
  return true;
}

const FORM_VACIO = { nombre:"", orden:"", color:"#00FF00", slug:"", logoURL:"", activo:true };

export default function GestionCompanias() {
  const { cuerpoId, cuerpoSlug } = useCuerpo();
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [logoArchivo, setLogoArchivo] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    if (!cuerpoId) return;
    const snap = await getDocs(query(collection(db,"companias"), where("cuerpo_id","==",cuerpoId)));
    setItems(snap.docs.map(d=>prepararCompania(d,cuerpoId)).sort(ordenarCompanias));
  };
  useEffect(()=>{ cargar(); },[cuerpoId]);

  const slugsUsados = useMemo(()=>new Set(items.filter(x=>x.id!==editId).map(x=>x.slugResuelto).filter(Boolean)),[items,editId]);

  const subirLogo = async (archivo, slug) => {
    if (!archivo) return form.logoURL.trim();
    const extension = (archivo.name.split(".").pop() || "png").replace(/[^a-zA-Z0-9]/g, "").toLowerCase() || "png";
    const carpetaCuerpo = String(cuerpoId || cuerpoSlug || "cuerpo").replace(/\//g, "-");
    const archivoRef = ref(storage, `logos_companias/${carpetaCuerpo}/${slug}.${extension}`);
    await uploadBytes(archivoRef, archivo, { contentType: archivo.type || undefined });
    return await getDownloadURL(archivoRef);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!cuerpoId) return;
    const slug = normalizarSlug(form.slug || form.nombre);
    if (!form.nombre.trim() || !slug) return alert("Nombre y slug son obligatorios.");
    if (slugsUsados.has(slug)) return alert("Ese slug ya está siendo usado por otra compañía de este Cuerpo.");
    if (!validarImagen(logoArchivo)) return;

    setGuardando(true);
    try {
      const logoURL = await subirLogo(logoArchivo, slug);
      const payload = { nombre:form.nombre.trim(), orden:String(form.orden || ""), color:form.color || "#00FF00", slug, logoURL, activo:Boolean(form.activo), cuerpo_id:cuerpoId };
      if (editId) await setDoc(doc(db,"companias",editId),payload,{merge:true});
      else await addDoc(collection(db,"companias"),payload);
      setForm(FORM_VACIO);
      setLogoArchivo(null);
      setEditId(null);
      setMensaje("Compañía guardada correctamente.");
      await cargar();
    } catch (error) {
      console.error(error);
      alert("No se pudo guardar la compañía: " + error.message);
    } finally {
      setGuardando(false);
    }
  };

  const editar = (x) => {
    setEditId(x.id);
    setForm({ nombre:x.nombre||"", orden:x.orden||"", color:x.color||"#00FF00", slug:x.slugResuelto||"", logoURL:x.logoResuelto||"", activo:x.activo!==false });
    setLogoArchivo(null);
    window.scrollTo({top:0,behavior:"smooth"});
  };
  const cancelar = () => { setEditId(null); setForm(FORM_VACIO); setLogoArchivo(null); };
  const eliminar = async (x) => { if (!window.confirm(`¿Eliminar ${x.nombre}?`)) return; await deleteDoc(doc(db,"companias",x.id)); await cargar(); };

  const logoPreview = logoArchivo ? URL.createObjectURL(logoArchivo) : form.logoURL;

  return <div style={{maxWidth:900}}>
    <h2>Compañías del Cuerpo</h2>
    <p>Estas compañías pertenecen a <strong>{cuerpoId}</strong>. El slug define la URL pública de cada compañía.</p>
    <form className="formulario" onSubmit={guardar} style={{display:"grid",gap:10}}>
      <input placeholder="Nombre" value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})}/>
      <input placeholder="Orden (1, 2, 3...)" value={form.orden} onChange={e=>setForm({...form,orden:e.target.value})}/>
      <input placeholder="Slug URL" value={form.slug} onChange={e=>setForm({...form,slug:normalizarSlug(e.target.value)})}/>

      <label style={{fontWeight:600}}>Logo de la compañía
        <input type="file" accept="image/*" onChange={e=>setLogoArchivo(e.target.files?.[0] || null)} style={{display:"block",marginTop:6}}/>
      </label>
      {logoPreview && <img src={logoPreview} alt="Vista previa" style={{maxWidth:130,maxHeight:130,objectFit:"contain",border:"1px solid #ddd",borderRadius:8,padding:6}}/>}

      <label>Color <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})}/></label>
      <label><input type="checkbox" checked={form.activo} onChange={e=>setForm({...form,activo:e.target.checked})}/> Compañía activa</label>
      <div><button type="submit" disabled={guardando}>{guardando?"Guardando...":(editId?"Guardar cambios":"Crear compañía")}</button>{editId&&<button type="button" onClick={cancelar} style={{marginLeft:8}}>Cancelar</button>}</div>
    </form>
    {mensaje&&<p>{mensaje}</p>}
    <table className="tabla" style={{width:"100%",marginTop:20}}><thead><tr><th>Logo</th><th>Orden</th><th>Nombre</th><th>Slug</th><th>Activa</th><th></th></tr></thead><tbody>{items.map(x=><tr key={x.id}><td>{x.logoResuelto?<img src={x.logoResuelto} alt="" style={{width:54,height:54,objectFit:"contain"}}/>:"—"}</td><td>{x.orden}</td><td>{x.nombre}</td><td>{x.slugResuelto}</td><td>{x.activo===false?"No":"Sí"}</td><td><button onClick={()=>editar(x)}>Editar</button> <button onClick={()=>eliminar(x)}>Eliminar</button></td></tr>)}</tbody></table>
  </div>;
}
